/**
 * Home: the conversation with the doorman that answers your door (owner
 * decisions 2026-08-03/04). A first visit IS onboarding — the door opened
 * itself with the permissive defaults the moment the thread was read, the
 * greeting says so, and the doorman's setup questions tune it from there:
 * chips draft answers into the composer, typing works just as well, and
 * "I'm done" leaves the door standing open.
 *
 * The Morning Nob opens the thread — the briefing was always the doorman
 * talking; this screen is where the owner talks back. Below it, the persisted
 * conversation; at the bottom, a composer and the meter — which states this
 * month's allowance as a SHARE and never as a figure (owner decision
 * 2026-08-21, ruling C2). Usage belongs beside the work; the capacity behind
 * the share stays server-side.
 *
 * A rules change never lands silently: the doorman proposes, the card shows
 * the diff, and Accept is the same accept the Rules page uses (M15). Dismiss
 * is equally final and equally cheap.
 *
 * AND A PROPOSAL IS ALWAYS A CARD, NEVER PROSE (staging walk, 2026-09-09). The
 * owner used to type "Accept" at prose that had proposed nothing, five times,
 * and get a document that had not changed a line.
 *
 * SETTING UP THE HOUSE RULES IS A CONVERSATION (ruling R-0915-1). There is no
 * section walk here any more — no step counter, no section order, no ending
 * sentence, and nothing sent in the owner's name between cards. Nob asks
 * ordinary questions, the owner answers them, and when Nob knows enough it
 * proposes ONE batch: a single card naming every section it touches, because
 * the owner reviews a change and not a queue.
 *
 * ADJUST IS A TURN, NOT A DOOR OUT (R-0915-1 (4), reversing R-0914-2 (3)). The
 * whole point of the product is that the owner never edits `nob.md` by hand, so
 * Adjust stopped opening the markdown editor. It focuses the composer and takes
 * no turn by itself; the sentence the owner sends next goes up with `adjust_of`
 * naming the card, and the answer is a revised card that supersedes it. The old
 * card is replaced where it stands by a line saying so. Nothing on this screen
 * opens the editor; the editor is reached from Settings.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acceptBioProposal,
  acceptPublicLineProposal,
  acceptSurfacedProposal,
  applyDoormanSetup,
  dismissBioProposal,
  dismissPublicLineProposal,
  dismissProposal,
  fetchDoormanThread,
  streamDoormanTurn,
  uploadAttachment,
  type Attachment,
  type DoormanMessage,
  type HearingRoads,
  type TrialOffer,
} from "../../lib/api";
import { SEE_THE_PLANS, START_TRIAL, trialOfferLine } from "../plan/trial-gate";
import { useRoomAdded } from "../../lib/room";
import { doormanThreadQuery, planQuery, qk } from "../../lib/queries";
import { track } from "../../lib/telemetry";
import {
  CHAT_FIELD,
  ChatShell,
  ChatLine,
  Composer,
  DoorHeader,
  DoormanBubble,
  NobBubbleFace,
  SystemRow,
  ThinkingBubble,
} from "../knock/chat";
import { runAt, runPositions, type RunItem } from "../../lib/runs";
import { filesFromClipboard } from "../../lib/linkify";
import { BubbleMenu, COPIED_COPY, QuoteBar, type MenuTarget } from "../knock/bubble-menu";
import {
  AttachButton,
  AttachmentBubble,
  DropOverlay,
  PendingChip,
  attachmentFailureLine,
  localRefusal,
  useFileDrop,
  ONE_AT_A_TIME,
} from "../knock/attachment";
import { useVoiceRecorder, VOICE_UNHEARD_COPY } from "../knock/voice";
import { VoiceButton, VoicePreview, type VoiceClip } from "../knock/voice-button";
import { useMessageActions } from "../knock/use-message-actions";
import { PLACEHOLDER_COPY, SetupQuestion } from "./setup-question";
import { DoorsThatOpened, useOpenedDoors } from "./door-opened";
import { DoormanPrompts } from "./prompts";
import { DoormanSuggestions, ONBOARDING_ASK } from "./suggestions";
import { DiffRow } from "../rules/diff-view";
import { diffLinesFromText } from "../../lib/api/rules";
import { SpentRow } from "../plan/usage-notice";
import { AI_DRAFT_LABEL } from "@nob/schemas";
import { RecapArea, RecapBarArea } from "../recap/recap-area";
import { useMetered } from "../shell/use-metered";
import { Button, LinkButton, QuietButton, SecondaryButton } from "../ui/button";
import { ForwardIcon, LockIcon } from "../ui/icons";

export const METER_LABEL = "a word with your doorman";

/**
 * THE FREE OWNER'S COMPOSER, LOCKED (ruling R-0825-4, the reference build's own
 * conversion gate).
 *
 * Free performs no AI work at all (ruling C10), so a Free owner asking their
 * doorman anything is a turn that cannot happen. Until now the field took the
 * words, sent them, and let the server refuse — which spent somebody's sentence
 * to tell them something the screen already knew, and bounced them back to a
 * composer holding their own text. The refusal belongs BEFORE the typing.
 *
 * A BAR, NOT A DISABLED FIELD. A greyed-out textarea says "broken"; a bar that
 * names the reason and opens the plans says "not yet, and here is the way". It
 * is the same height and sits in the same slot as the composer it replaces, so
 * nothing on the screen moves when the gate lifts.
 */
export const LOCKED_TITLE = "Chatting with your Nob takes a plan";
/**
 * The bar's second line (wave 83): what Free DOES do, then the way on. "Your
 * Nob doesn't talk on Free" sat under knocks Nob had fully heard, and read as
 * if Nob did nothing at all on Free.
 */
export const LOCKED_REASSURE = "It still hears your knockers.";
export const LOCKED_ACTION = `${LOCKED_REASSURE} ${SEE_THE_PLANS}`;
export const ACCEPT_COPY = "Accept";
/**
 * THREE VERBS, ON EVERY CARD (ruling R-0915-1). There used to be two sets — one
 * for a proposal the owner had asked for by name, one for a section the
 * walkthrough was working through on its own initiative — and each card had to
 * be told which world it was in. There is one world now: the conversation
 * proposes, and the owner takes it, says how it should read instead, or leaves
 * it.
 *
 * NOT NOW, rather than Dismiss. "Dismiss" was the honest opposite of Accept
 * while a proposal was a thing the owner had asked for. A batch drawn from a
 * conversation is Nob's own read of what was said, and declining it is not a
 * verdict on a request — it is "leave my rules as they are". The proposal
 * closes either way; the word is what the owner is actually doing.
 *
 * ADJUST IS NOT AN EDITOR AND NO LONGER OPENS ONE (R-0915-1 (4)). It focuses
 * the composer and takes no turn by itself; what the owner sends next carries
 * `adjust_of`, and the answer is a revised card.
 */
export const ADJUST_COPY = "Adjust";
export const NOT_NOW_COPY = "Not now";
export const NOT_NOW_SETTLED_COPY = "Not now. Nothing changed.";
/** The composer, while a card is being adjusted — the field says what it wants. */
export const ADJUST_PLACEHOLDER = "Tell Nob what to change";
/**
 * WHAT BECAME OF THE CARD THE OWNER ADJUSTED. One line where the card stood,
 * and never a card taken out from under a reader: they pressed a verb on it a
 * moment ago, and a slot that simply emptied would read as a crash.
 */
export const REVISED_COPY = "Revised. The new card is below.";
/**
 * THE ONE LINE A RUNNING CONVERSATION GETS (ruling R-0915-1). Not a step
 * counter and nothing numeric: there is no fixed number of questions any more,
 * so a figure here would be invented. It says what is happening, and it goes
 * when the batch goes up.
 */
export const RUNNING_COPY = "Nob is getting to know your door.";
/** The rules card's eyebrow, and what settles in place of its buttons. */
export const RULES_PROPOSED_COPY = "Rules change proposed";
export const RULES_ACCEPTED_COPY = "Accepted. The rules carry it now.";
/** The bio card's own eyebrow, and what settles in place of its buttons. */
export const BIO_PROPOSED_COPY = "Bio proposed";
export const BIO_ACCEPTED_COPY = "Accepted. Your door says it now.";
/** The public line's card (R-0925-2 (5)): the door's other public text. */
export const PUBLIC_LINE_PROPOSED_COPY = "Public line proposed";
export const PUBLIC_LINE_ACCEPTED_COPY = "Accepted. Your door shows it under What gets through.";
export const RETRY_COPY = "Your doorman lost the thread. Say that again.";
/**
 * THE WAIT, IN NOB'S VOICE (ruling R-0915-5). The onboarding conversation runs
 * on grok-4.6 now, which thinks for a p95 of 144 seconds — long enough that a
 * pane with nothing in it reads as a hang rather than as work. So the spot
 * where the reply is about to appear says what is happening, in the same
 * register as the running-conversation line above the log: one sentence, no
 * counter, no elapsed seconds, nothing to watch.
 */
export const THINKING_COPY = "Nob is thinking";
/**
 * …AND IT WAITS BEFORE IT SAYS IT. A fast turn — the deterministic paths, a
 * cached prefix, a short answer — puts its first word up in well under half a
 * second, and a line that appeared and vanished in that time is a flash, which
 * reads as a glitch rather than as patience. Below this, the pane simply stays
 * as it was until the words arrive.
 */
export const THINKING_DELAY_MS = 400;
/** The sentence beside "Get more usage" when a turn was refused for want of room. */
export const BROKE_COPY = "That needs more room than this month has left.";

/**
 * THE PROTOCOL TAGS A REPLY MAY TRAIL (rules/protocol.ts). Never shown — not
 * for an instant, and not in the half-written state a stream arrives in.
 *
 * ALL FOUR, since R-0915-5. The two payload tags were held back from the
 * moment this pane streamed anything; the two marker tags were not, on the
 * reasoning that the api strips them at persist time and the flash was
 * theoretical. It was not: two of five grok-4.3 openers in the onboarding
 * harness ended `… worth your time? <ask/>`, which the api removed from the
 * row and the console had already drawn (`evals/results/REPORT-onboarding.md`).
 * A tag is the api's business with the model; an owner who sees one has been
 * shown the machinery.
 */
const TRAILING_TAGS = [
  "<ask",
  "<rules_ask",
  "<rules_instruction",
  "<bio_proposal",
  "<public_line_proposal",
];

/**
 * What of a streamed reply may be drawn. One function, because the same text
 * is rendered twice — live, as it arrives, and again as the settled line the
 * moment the turn ends — and a strip that ran on only the first of those is
 * how a tag reaches the screen in the last frame before the refetch.
 *
 * MID-STREAM a trailing partial is held back: `<` then `ask` then `/>` arrive
 * as separate chunks as often as one, so anything that could still become a tag
 * waits until it either completes or turns out to be ordinary prose. ONCE THE
 * STREAM HAS ENDED nothing more can arrive, so a dangling `<` is exactly what
 * it looks like — prose — and is kept.
 *
 * Nothing here touches what the api persists: the row is the api's own strip
 * (`rules/protocol.ts`), and this is only about the frames in between.
 */
function visibleProse(streamed: string, ended = false): string {
  const starts = TRAILING_TAGS.map((tag) => streamed.indexOf(tag)).filter((at) => at >= 0);
  const tagStart = starts.length > 0 ? Math.min(...starts) : -1;
  if (tagStart >= 0) return streamed.slice(0, tagStart);
  if (ended) return streamed;
  const lt = streamed.lastIndexOf("<");
  return lt >= 0 && TRAILING_TAGS.some((tag) => tag.startsWith(streamed.slice(lt)))
    ? streamed.slice(0, lt)
    : streamed;
}

/**
 * The composer's meter, in the PLAN's own terms and as a SHARE (docs/plans.md
 * §5.1, §5.3; economics-wire §2).
 *
 * **ONE ALLOWANCE, BOTH DIRECTIONS** (ruling C3). It used to say "words with
 * your doorman", which was true when a plan metered only the owner's own side.
 * One plan now meters both — the work an owner's Nob does for them AND the work
 * their knocking asks of somebody else's Nob — so the sentence says
 * **allowance**, which is the thing that is actually being spent, whichever
 * side of a door spent it. A meter that named only one side would be understating
 * what a turn costs the person reading it.
 *
 * A FRACTION, IN PROSE, AND NEVER A NUMBER OF ANYTHING. What somebody needs
 * mid-conversation is whether there is room, and the honest unit for that is a
 * proportion of the period. A count would be a capacity, and capacities stay
 * server-side; a price would be a rate card at the moment of use, which is the
 * model this product retired. The arithmetic behind the share never leaves the
 * server.
 *
 * Coarse on purpose. The buckets are wide because the sentence is meant to be
 * read at a glance and forgotten, and because a share precise enough to divide
 * back into a capacity would be the capacity, said slowly.
 *
 * AT OVERFLOW IT SAYS NOTHING (owner, 2026-09-23; wave 61). It used to say
 * "this month's allowance is used — work waits until it resets" in grey mono
 * under the composer, directly beneath the row that had just said the same
 * thing with a "Get more usage" beside it. The fact that the room ran out is
 * still never withheld — the thread's own spent row says it from the moment the
 * period is used, with "Get more usage" beside it — it is just said once, by
 * the thing that offers a way out.
 */
/** The composer's line past 100% with pay as you go on: where, never how much. */
export const PAST_ALLOWANCE_LINE = "past this month's allowance";

export function meterLine(plan: { used_share: number; overflow: boolean }): string | null {
  if (plan.overflow) return null;
  // PAST THE ALLOWANCE, AND STILL WORKING (docs/plans.md §13 Q21): pay as you
  // go is on, so the room has not run out. No money and no number here, as
  // every other arm of this sentence — the dollars are billing's, and the
  // composer never says what a turn costs (R-0923-3 (8)).
  if (plan.used_share > 1) return PAST_ALLOWANCE_LINE;
  const left = 1 - Math.min(Math.max(plan.used_share, 0), 1);
  const share =
    left > 0.85
      ? "nearly all"
      : left > 0.6
        ? "most"
        : left > 0.4
          ? "about half"
          : left > 0.2
            ? "about a third"
            : "a little";
  return `${share} of this month's allowance left`;
}

/**
 * What became of a proposal, in this sitting.
 *
 * Three, not five. `skipped` and `adjusted` were local refinements of the
 * server's `dismissed` that the walkthrough needed, and both go with it (ruling
 * R-0915-1): there is no Skip any more, and Adjust dismisses nothing.
 *
 * `revised` is not a verdict the owner gave — it is what became of a card when
 * the adjusted version of it came back. It is a state of the card all the same,
 * because the card it replaces has to say so where it stands; the server's own
 * word for the same fact is `superseded`, and a reload reads that instead.
 */
type Verdict = "accepted" | "dismissed" | "revised";

/**
 * WHICH SECTIONS A CARD TOUCHES, kept beside the thread rather than read off it.
 *
 * The stream says it once, on the `proposal` frame. The live card yields to its
 * persisted twin a second later and `GET /doorman/thread` carries no `sections`
 * at all — so without a map of its own the chips would flash once and vanish,
 * which is worse than never drawing them.
 */
type CardSections = Record<string, string[]>;

type LiveLine =
  | { kind: "owner" | "doorman" | "notice"; content: string }
  /** A file that has just landed, before the thread is read back. */
  | { kind: "attachment"; attachment: Attachment }
  | { kind: "proposal"; proposalId: string; diff: string; changeNote: string }
  /**
   * A bio the doorman drafted (FR-3.8.1) — a different card, a different verb —
   * or a public line (R-0925-2 (5)), which is the same card about the door's
   * other public text: `target` says which, and so which endpoint answers it.
   */
  | {
      kind: "bio_proposal";
      proposalId: string;
      changeNote: string;
      target: "bio" | "public_line";
    };

type Props = {
  onSessionExpired: () => void;
  onSeeAll: () => void;
  /**
   * The way to the House Rules, for the doorman's third standing prompt. A
   * callback rather than a navigator because this component renders outside a
   * router in the interaction tier — the same arrangement `onSeeAll` has.
   */
  onEditRules: (() => void) | null;
  /**
   * The way to the share sheet, for the doorman's second standing prompt. The
   * shell owns that sheet — it is the same one the desk and the pane header
   * open — so the prompt asks for it rather than building a second one inside
   * the thread (FR-11, owner 2026-08-25).
   */
  onShareDoor: () => void;
  /**
   * Open one knock's conversation in the console — the Morning Nob's only verb
   * (owner, 2026-08-26). A callback for the same reason `onSeeAll` is one: this
   * file renders outside the shell in the interaction tier, and the shell owns
   * which conversation is open. Absent means the briefing's cards have nowhere
   * to go, and they say nothing rather than offering a dead link.
   */
  onOpenKnock?: ((knockId: string) => void) | undefined;
  /**
   * Home's console, handed through to the chat column's masthead slot. It is
   * passed rather than mounted here because it is not part of the
   * conversation: this file owns what the doorman says, and the summaries
   * above it are Home's business (routes/index.tsx).
   */
  masthead?: ReactNode;
  /** The door exists and is paused — the shelf carries the way back up. */
  doorPaused?: boolean;
  onReopenDoor?: (() => void) | undefined;
  /**
   * The shell owns the chrome.
   *
   * Inside the messenger shell this conversation is a pane, not a page: the
   * pane draws the header (whose door, which state, the way to the desk) and
   * gives the column its height. Two headers stacked, or a column measuring
   * itself off the viewport inside a flex parent, are the two things this flag
   * exists to prevent.
   */
  embedded?: boolean;
  /**
   * Words put in the composer by something outside the conversation — the
   * desk's "personalize your house rules", the share sheet's "ask Nob to write
   * you one".
   *
   * It DRAFTS by default, and SENDS when `send` is set. Drafting is the right
   * default: a button that spoke on the owner's behalf would be the product
   * having the conversation for them. But a button whose entire label IS the
   * request has already been agreed to by being pressed — leaving its sentence
   * sitting in the box for a second Enter reads as the app not having heard,
   * which is what the owner reported (2026-08-25). The nonce is what lets the
   * same ask be made twice.
   *
   * `adjustOf` is the third shape, and it says nothing at all: Adjust pressed on
   * a card in "Nob has noticed" (ruling R-0915-1 (4)) opens this conversation
   * with the composer focused and the card named, so the owner's next sentence
   * is the revision. It carries no words, because the words are theirs.
   */
  prefill?: { text: string; nonce: number; send?: boolean; adjustOf?: string } | null;
  /**
   * The way to the plans (§5.4). A Free owner's composer is a locked bar rather
   * than a field (ruling R-0825-4) and this is what the bar opens; a callback
   * rather than a navigator, for the same reason `onSeeAll` is one.
   */
  onSeePlans?: (() => void) | undefined;
  /** The locked bar's way to the trial, while one is on offer (ruling R-1006-2). */
  onStartTrial?: (() => void) | undefined;
  /**
   * Take a line to a door that has just opened (owner item 3, `door-opened.tsx`).
   *
   * A callback rather than this component doing it, for the same reason
   * `onSeePlans` is one: knocking is the shell's act — its compose thread, its
   * draft machinery — and this conversation only knows that somebody who was
   * waiting now has somewhere to knock. Absent means the shelf is not drawn at
   * all and its read is never made, which is what keeps this component
   * renderable outside the shell.
   */
  onKnockAt?: ((handle: string, line: string) => void) | undefined;
};

export function DoormanArea({
  onSessionExpired,
  onSeeAll,
  onEditRules,
  onShareDoor,
  onOpenKnock,
  masthead = null,
  embedded = false,
  prefill = null,
  onSeePlans,
  onStartTrial,
  doorPaused = false,
  onReopenDoor,
  onKnockAt,
}: Props) {
  const queryClient = useQueryClient();
  const thread = useQuery(doormanThreadQuery());
  /**
   * The plan, for the one control's label (wave 57): "Get more usage", or
   * "Start a plan" on a trial or on Free — the server's word, on every read.
   * Null while plans are dark, and then a 402 is answered by its own sentence
   * and nothing else, because there is no plan sheet to open.
   */
  const plan = useQuery(planQuery());
  /**
   * WHICH TIER THIS SESSION IS ON — asked in the ONE place that answers it
   * (`use-metered.ts`, a N-3).
   *
   * This column used to read `thread.plan.metered` off its own payload, on the
   * argument that a second read could disagree with the meter rendered beside
   * it. It could and it did, the other way round: a plan bought in the plan
   * room invalidates the plan lane, and the doorman thread is not in that
   * invalidation — so the room read `NOW · Lite` while the column next to it
   * still carried "Your Nob doesn't talk on Free / See plans", in one frame,
   * until a reload. Two sources for one fact is how a console names two tiers.
   *
   * `useMetered` prefers the plan lane and falls back to this very thread, so
   * where plans are dark the answer is identical to the old one.
   */
  const metered = useMetered();
  /**
   * Doors this person was waiting at that have since opened (owner item 3).
   * A hook, so it sits with the others above every early return; the shelf it
   * feeds is composed below, once there is a thread to hang it over.
   */
  const openedDoors = useOpenedDoors(onKnockAt !== undefined);

  const [draft, setDraft] = useState("");
  const [live, setLive] = useState<LiveLine[]>([]);
  const [streaming, setStreaming] = useState<string | null>(null);
  /**
   * The thinking line is DUE — the turn has been waiting longer than
   * `THINKING_DELAY_MS` with nothing to show (R-0915-5). Not "the turn is
   * busy": that is `busy`, and rendering on it alone is what flashes.
   */
  const [thinkingDue, setThinkingDue] = useState(false);
  /** The doorman is drafting an edit — the long pause between reply and card. */
  const [working, setWorking] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * THE 402'S OWN FACTS — the three roads out of it (ToU 2.2 §5.3) — so a
   * refused turn is answered with a way on rather than with a sentence about
   * money. It held a shortfall and a shelf of packs until ruling R-0916-1, and
   * the priced reset until wave 57: what it answers with now is one control.
   */
  const [exhausted, setExhausted] = useState<HearingRoads | null>(null);
  /** Proposals answered in this sitting, so their cards settle in place. */
  const [resolved, setResolved] = useState<Record<string, Verdict>>({});
  /** The sections each card names, as the stream said them. See `CardSections`. */
  const [cardSections, setCardSections] = useState<CardSections>({});
  /**
   * The card the owner pressed Adjust on (ruling R-0915-1 (4)). Nothing has been
   * written and nothing has been sent: it is the composer's subject until the
   * owner says what to change, and the send below carries it as `adjust_of`.
   */
  const [adjusting, setAdjusting] = useState<string | null>(null);
  /**
   * A House Rules conversation is running (ruling R-0915-1) — AS THE SERVER
   * SAYS. This used to be the console's own memory of having sent the opening
   * sentence, and a reload wiped it.
   *
   * The run is a fact about the thread, so it is READ off the thread: on load,
   * and off every turn's `done` frame. One state and no ref any more — nothing
   * inside a running stream decides anything by it, because the verbs on a card
   * no longer change with the run.
   */
  const [running, setRunning] = useState(false);
  /** Labels drafted into the composer by chip taps, in tap order. An exact
   * match at send time takes the deterministic setup path (free, instant);
   * one edited character makes it an ordinary turn — the chat is the
   * mechanism, the fast path just an implementation detail. */
  const [drafted, setDrafted] = useState<string[]>([]);
  /** Questions set aside this sitting; the course resumes next visit. */
  const [skippedSetup, setSkippedSetup] = useState<Set<string>>(new Set());
  /** Older pages, loaded backwards on demand — the history is kept, and the
   * newest-page read stays cheap. `null` until we know there may be more. */
  const [earlier, setEarlier] = useState<DoormanMessage[]>([]);
  const [maybeMore, setMaybeMore] = useState(true);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  /**
   * The Morning Nob is up (owner, 2026-08-26). OPEN BY DEFAULT — the console
   * opens on the briefing, which is the whole argument for the briefing being
   * home. Held here rather than in `RecapArea` because the card floats over the
   * log and the minimised bar hangs in the masthead: one fact, two slots on
   * opposite sides of the scroller. Deliberately not remembered — a briefing is
   * a thing you read once, and yesterday's collapse is not a preference.
   */
  const [recapOpen, setRecapOpen] = useState(true);

  const expired = useRef(onSessionExpired);
  expired.current = onSessionExpired;
  const busyRef = useRef(false);
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);
  const abort = useRef<AbortController | null>(null);
  useEffect(() => () => abort.current?.abort(), []);

  /**
   * THE THINKING LINE'S CLOCK (R-0915-5). Armed when a turn goes and whenever
   * the pane is left waiting again mid-turn — after a notice, which settles
   * what was said and starts a second call — and stopped by the first rendered
   * character, or by the turn ending either way. A ref, not state: nothing
   * renders from the timer itself, and a pending one must survive the renders
   * the stream causes.
   */
  const thinkTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const armThinking = useCallback(() => {
    if (thinkTimer.current) clearTimeout(thinkTimer.current);
    setThinkingDue(false);
    thinkTimer.current = setTimeout(() => setThinkingDue(true), THINKING_DELAY_MS);
  }, []);
  const stopThinking = useCallback(() => {
    if (thinkTimer.current) clearTimeout(thinkTimer.current);
    thinkTimer.current = null;
    setThinkingDue(false);
  }, []);
  useEffect(
    () => () => {
      if (thinkTimer.current) clearTimeout(thinkTimer.current);
    },
    [],
  );

  // The field grows with what is typed — same move as the hearing's
  // TurnInput: measure after every draft change, capped by the class's
  // max-h-40, and a chip-drafted multi-line prompt arrives readable instead
  // of scrolling inside one visual line.
  const field = useRef<HTMLTextAreaElement>(null);

  /**
   * Copy and quote, the same two the hearing offers and behaving identically —
   * and, since 2026-08-23, the paperclip and the mic beside them. The thread
   * has somewhere to keep a file now (`attachments.owner_id`), so the
   * affordance is offered here for the same reason it is offered there and
   * behaves the same way down to the preview.
   */
  const actions = useMessageActions(() => field.current?.focus());

  /** The file going up right now, and the one sentence a refusal gets. */
  const [pendingFile, setPendingFile] = useState<{ filename: string; size: number } | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const uploading = useRef<AbortController | null>(null);

  /**
   * The recorder, held HERE rather than inside the mic: two parts of the
   * composer read it — the button that starts and stops, and the preview above
   * the field that plays the clip back before it goes anywhere. Exactly the
   * arrangement `thread.tsx` uses, and the same hook.
   */
  const voice = useVoiceRecorder();

  useLayoutEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = "0px";
    // scrollHeight is the content box; the element is border-box, so without
    // the border the field sits 2px short and grows a phantom scrollbar.
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = `${Math.min(el.scrollHeight + border, 160)}px`;
  }, [draft]);

  /**
   * The card an adjusted one replaced. Recorded the moment the stream names it,
   * so the old card turns into its one-line note in the same frame the new card
   * arrives — and the note survives the refetch, because the persisted row then
   * carries `superseded` and says the same thing.
   */
  const supersede = useCallback((proposalId: string) => {
    setResolved((prev) => ({ ...prev, [proposalId]: "revised" }));
  }, []);

  const sendMessage = useCallback(
    /**
     * `adjustOf` names the card this sentence revises (ruling R-0915-1 (4)). It
     * is an ordinary turn in every other respect — same endpoint, same stream,
     * same meter — and that is the point of it: Adjust is talking, not editing.
     */
    (
      raw: string,
      adjustOf: string | null = null,
      /**
       * A VOICE NOTE SENT AS THE TURN (wave 76). `raw` is then its transcript,
       * for this screen's own bookkeeping; what goes up is the note's id, and
       * the server reads the words off the note. The owner's line is the note's
       * bubble — the player, the words under it — not a second, typed-looking
       * bubble beside it.
       */
      voice: Attachment | null = null,
    ) => {
      const message = raw.trim();
      if (!message || busyRef.current) return;
      /**
       * The run is the SERVER'S word, arriving on the `done` frame below. This
       * is the optimistic half and nothing more: the opening ask answers with a
       * question, and the frame that follows says whether a run is up.
       */
      if (message === ONBOARDING_ASK) setRunning(true);
      setBusy(true);
      armThinking();
      setError(null);
      setExhausted(null);
      // A voice turn leaves the composer alone: nothing in it was sent.
      if (!voice) setDraft("");
      // The owner's line goes up as THIS object, so a failed turn can take
      // exactly it back out — never whatever happens to sit last, which
      // mid-stream can be the doorman's own settled line.
      const ownerLine: LiveLine = voice
        ? { kind: "attachment", attachment: voice }
        : { kind: "owner", content: message };
      setLive((prev) => [...prev, ownerLine]);
      track({ name: "doorman_turn" });

      const controller = new AbortController();
      abort.current = controller;

      void (async () => {
        let spoken = "";
        /** The server's `done` frame arrived: only then did the turn land. */
        let settled = false;
        /** A rendered character has arrived — see the silent-turn guard below. */
        let heard = false;
        let failure: string | null = null;
        for await (const event of streamDoormanTurn(
          message,
          controller.signal,
          adjustOf,
          voice?.attachment_id ?? null,
        )) {
          if (event.type === "token") {
            spoken += event.delta;
            // The trailing tags are protocol, not conversation: the api strips
            // them at persist time, and the live render must not flash them in
            // the meantime. Re-read on every chunk, because a tag arrives split
            // as often as whole.
            const shown = visibleProse(spoken);
            setStreaming(shown);
            // The thinking line goes on the first character the OWNER sees, not
            // on the first byte off the wire: a reply that opens with a held
            // back tag fragment has nothing to show yet, and swapping the line
            // for an empty bubble would be a pane that went blank.
            if (shown.length > 0) {
              heard = true;
              if (thinkTimer.current) stopThinking();
            }
          }
          if (event.type === "notice") {
            setWorking(null);
            // Whatever was said before the notice is SETTLED prose: a turn can
            // speak twice now — a walkthrough reply that proposed nothing draws
            // the server's own notice and one more attempt after it (browser
            // walk, 2026-09-10) — and the two must not run together into one
            // bubble that is then persisted as two.
            const beforeNotice = visibleProse(spoken, true).trim();
            if (beforeNotice)
              setLive((prev) => [...prev, { kind: "doorman", content: beforeNotice }]);
            spoken = "";
            setStreaming(null);
            setLive((prev) => [...prev, { kind: "notice", content: event.content }]);
            // A notice ends one call and starts another, so the pane is waiting
            // again with nothing to show: the clock restarts rather than the
            // line coming straight back.
            armThinking();
          }
          if (event.type === "working") {
            setWorking(event.content);
            // The reply is finished prose now; let it settle as a live line so
            // the drafting indicator reads as what happens NEXT.
            setLive((prev) => [
              ...prev,
              { kind: "doorman", content: visibleProse(spoken, true).trim() },
            ]);
            spoken = "";
            setStreaming(null);
          }
          if (event.type === "proposal") {
            setWorking(null);
            setCardSections((prev) => ({ ...prev, [event.proposal_id]: event.sections }));
            // The card this one revises is replaced WHERE IT STANDS, not left
            // standing beside its own replacement (R-0915-1 (4)). The server
            // says which; nothing here infers it from the order things arrived.
            if (event.supersedes) supersede(event.supersedes);
            setLive((prev) => [
              ...prev,
              {
                kind: "proposal",
                proposalId: event.proposal_id,
                diff: event.diff,
                changeNote: event.change_note,
              },
            ]);
          }
          if (event.type === "bio_proposal" || event.type === "public_line_proposal") {
            setWorking(null);
            if (event.supersedes) supersede(event.supersedes);
            setLive((prev) => [
              ...prev,
              {
                kind: "bio_proposal",
                proposalId: event.proposal_id,
                changeNote: event.change_note,
                target: event.type === "bio_proposal" ? "bio" : "public_line",
              },
            ]);
          }
          if (event.type === "done") {
            settled = true;
            // The run, as the server derived it from the thread this turn just
            // wrote to. A frame that carries nothing says nothing — it is never
            // read as an ending.
            if (event.onboarding) setRunning(event.onboarding.running);
          }
          if (event.type === "error") {
            if (event.code === "UNAUTHENTICATED") return expired.current();
            if (event.code === "PLAN_EXHAUSTED") {
              failure = BROKE_COPY;
              if (event.exhausted) setExhausted(event.exhausted);
            } else {
              failure = RETRY_COPY;
            }
          }
        }
        // A stream that stopped without `done` did not take the turn — the
        // same guard the hearing keeps (started.tsx): no turn taken is a turn
        // worth trying again, never a success to commit.
        if (failure === null && !settled && !controller.signal.aborted) failure = RETRY_COPY;
        /**
         * A TURN THAT SAID NOTHING AT ALL IS A FAILED TURN (R-0915-5). The api
         * streams the reply's prose before it decodes anything from it, so a
         * stream that closed cleanly with no characters in it is a model that
         * produced none — there is no card and no notice behind it either. The
         * thinking line has nowhere to go from there: leaving it to vanish
         * would end the wait in silence, so it becomes the sentence the other
         * broken turns get, with the owner's words back in the box.
         */
        if (failure === null && !heard && !controller.signal.aborted) failure = RETRY_COPY;
        if (failure !== null) {
          // The words go back where they were typed; the unsettled reply and
          // the owner's line come off the thread. A VOICE note stays where it
          // is: it is in the thread, uploaded, and the next turn announces it
          // — and nobody typed its words, so there is nothing to hand back.
          if (!voice) {
            setDraft((current) => (current.trim() ? current : message));
            setLive((prev) => prev.filter((line) => line !== ownerLine));
          }
          setStreaming(null);
          setWorking(null);
          stopThinking();
          setBusy(false);
          setError(failure);
          return;
        }
        // The last frame before the refetch, and the one the tag used to reach:
        // the settled line is the stripped prose, not the raw stream.
        const said = visibleProse(spoken, true).trim();
        if (said) setLive((prev) => [...prev, { kind: "doorman", content: said }]);
        setStreaming(null);
        setWorking(null);
        stopThinking();
        setBusy(false);
        // The follow-up turn below reads this ref, and the effect that mirrors
        // `busy` into it has not run yet — the guard would drop the send it is
        // there to allow. Same value the effect is about to write.
        busyRef.current = false;
        /**
         * A TURN THAT PROPOSES NOTHING NEVER ENDS THE RUN HERE. Nothing is
         * inferred from silence: the server derives the run off the thread and
         * says so on the frame above, and a reply with no card is the ordinary
         * case now — it is the next question (ruling R-0915-1 (2)).
         */
        // The meter moved; the thread persisted. Re-read it — and only once the
        // refetch has landed do the live lines yield to their persisted copies,
        // or the turn renders twice: once as state, once as history.
        await queryClient.invalidateQueries({ queryKey: qk.doormanThread() });
        setLive((prev) =>
          prev.filter((line) => line.kind === "proposal" || line.kind === "bio_proposal"),
        );
      })();
    },
    [armThinking, queryClient, stopThinking, supersede],
  );

  /**
   * THE RUN, RESUMED. Every load of the thread carries whether a House Rules
   * conversation is up, so a reload mid-run comes back where it stood. Nothing
   * here decides; it copies what the server derived.
   */
  const threadRun = thread.data?.ok ? thread.data.thread.onboarding.running : null;
  useEffect(() => {
    if (threadRun !== null) setRunning(threadRun);
  }, [threadRun]);

  /**
   * An ask from outside the conversation, taken once.
   *
   * Only on a NEW nonce: without the guard every re-render of the shell would
   * put the same sentence back over whatever the owner had started typing —
   * or, worse now, say it again. `send` decides which of the two happens; see
   * the `prefill` prop for why a button that IS the request says it.
   */
  const lastAsk = useRef(0);
  useEffect(() => {
    if (!prefill || prefill.nonce === lastAsk.current) return;
    lastAsk.current = prefill.nonce;
    setDrafted([]);
    // Adjust, arriving from the rules queue: the card is named, the box is
    // empty, and the owner's own sentence is the whole of the turn.
    if (prefill.adjustOf) {
      setAdjusting(prefill.adjustOf);
      setDraft(prefill.text);
      field.current?.focus();
      return;
    }
    if (prefill.send) {
      sendMessage(prefill.text);
      return;
    }
    setDraft(prefill.text);
    field.current?.focus();
  }, [prefill, sendMessage]);

  /**
   * A file into the owner's own thread, up the moment it is chosen — the same
   * shape the hearing's `putUp` has, against the doorman target.
   *
   * NOTHING IS SAID ABOUT MONEY, because nothing is spent: attaching is not a
   * turn, and the meter under the composer does not move for it. The next thing
   * the owner types is what gets metered, exactly as before.
   *
   * A VOICE NOTE IS A TURN (wave 76, `asTurn`): once it lands with a
   * transcript it is sent as the owner's line, and THAT turn is metered like
   * any other. A note with no transcript sends nothing — it stays as the file
   * it is, and Nob asks for it typed, once. A turn already in flight when the
   * note lands leaves it as a file too: the doorman is told of it on the next.
   */
  const putUp = useCallback(
    (file: File, durationMs: number | null, asTurn = false) => {
      setAttachError(null);
      setPendingFile({ filename: file.name, size: file.size });

      const controller = new AbortController();
      uploading.current = controller;

      void (async () => {
        const r = await uploadAttachment({ doorman: true }, file, controller.signal, durationMs);
        // Changed their mind while it was going up: whatever the server said
        // about a file nobody wants is not news.
        if (controller.signal.aborted) return;
        uploading.current = null;
        setPendingFile(null);

        if (!r.ok) {
          if (r.code === "UNAUTHENTICATED") return expired.current();
          setAttachError(attachmentFailureLine(r.code));
          return;
        }
        const note = r.attachment;
        if (asTurn && note.transcript !== null && !busyRef.current) {
          sendMessage(note.transcript, null, note);
          return;
        }
        // Into the thread where it was put. The persisted copy takes over on
        // the next read, in the same place — the server orders by the clock.
        setLive((prev) => [...prev, { kind: "attachment", attachment: note }]);
        if (asTurn && note.transcript === null) setError(VOICE_UNHEARD_COPY);
      })();
    },
    [sendMessage],
  );

  const attach = useCallback(
    (file: File) => {
      if (pendingFile) {
        setAttachError(ONE_AT_A_TIME);
        return;
      }
      const refused = localRefusal(file);
      if (refused) {
        setAttachError(attachmentFailureLine(refused));
        return;
      }
      putUp(file, null);
    },
    [pendingFile, putUp],
  );

  /**
   * A recording, up the same road. No `localRefusal`: that checks the PICKER's
   * extensions, and a voice note is not something anybody picked — the recorder
   * named it from the type it actually wrote.
   */
  const voiceNote = useCallback(
    (clip: VoiceClip) => {
      if (pendingFile) {
        setAttachError(ONE_AT_A_TIME);
        return;
      }
      putUp(new File([clip.blob], clip.filename, { type: clip.blob.type }), clip.durationMs, true);
    },
    [pendingFile, putUp],
  );

  /** The ✕ on the chip: stop the upload, and forget it happened. */
  const dropAttachment = useCallback(() => {
    uploading.current?.abort();
    uploading.current = null;
    setPendingFile(null);
  }, []);

  const drop = useFileDrop({ onFile: attach });

  /**
   * ROOM WAS ADDED, AND THE REFUSAL GOES WITH IT.
   *
   * There was a whole checkout here — a pack, a modal over this conversation
   * (ruling R-0825-2), a webhook to wait on — and ruling R-0916-1 deleted every
   * part of it. Since wave 57 the purchase is not even on this pane: "Get more
   * usage" opens the plan sheet, and the upgrade bought there is announced
   * (`lib/room.ts`). All that is left for this pane is to re-read the
   * share it just refilled and put the refusal away — its sentence too, which
   * would otherwise stand under a month that now has room.
   *
   * The refused TURN is not re-sent, unlike the hearing's. The owner's words
   * came straight back into the composer they were typed in and are sitting
   * there; sending them for somebody who is looking at them is the pane
   * deciding it had heard a second press.
   */
  const resetLanded = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: qk.plan() });
    setExhausted(null);
    setError((current) => (current === BROKE_COPY ? null : current));
  }, [queryClient]);
  // Every time, not only after a refusal: the standing row below is drawn from
  // the plan read, and a period refilled before anything was refused has to
  // put that row away too (wave 61).
  useRoomAdded(resetLanded);

  const setup = thread.data?.ok ? thread.data.thread.setup : null;
  const activeQuestion = setup?.pending.find((q) => !skippedSetup.has(q.key)) ?? null;
  /** A card still waiting for Accept/Dismiss. Persisted rows carry no
   * status, so only the NEWEST message counts — an old dismissed proposal
   * must not silence the question forever. */
  const lastMessage = thread.data?.ok ? thread.data.thread.messages.at(-1) : undefined;
  /** The conversation is mid-flow: the doorman just asked something, or the
   * owner's line is still the last word. Standing prompts butting in there
   * would derail the exchange — they wait for a resting point. */
  const midFlow =
    lastMessage !== undefined &&
    (lastMessage.role === "owner" ||
      (lastMessage.role === "doorman" && lastMessage.content.trimEnd().endsWith("?")));
  const pendingProposal =
    live.some(
      (l) =>
        (l.kind === "proposal" || l.kind === "bio_proposal") &&
        resolved[l.proposalId] === undefined,
    ) ||
    (lastMessage?.role === "notice" &&
      lastMessage.proposal_id !== null &&
      // `pending` and nothing else. A superseded card is settled — the owner
      // adjusted it and the replacement is the one still asking.
      lastMessage.proposal_status === "pending" &&
      resolved[lastMessage.proposal_id] === undefined);

  /** One deterministic setup answer: shown as the owner's line, applied at
   * once, answered in voice by the refetched thread. */
  const applySetup = useCallback(
    (
      message: string,
      question: string,
      picks: Parameters<typeof applyDoormanSetup>[0],
      count: number,
    ) => {
      setBusy(true);
      setError(null);
      setDraft("");
      setDrafted([]);
      setLive((prev) => [...prev, { kind: "owner", content: message }]);
      void applyDoormanSetup(picks).then(async (r) => {
        setBusy(false);
        if (!r.ok) {
          if (r.code === "UNAUTHENTICATED") return expired.current();
          setDraft(message);
          setLive((prev) => prev.slice(0, -1));
          setError(RETRY_COPY);
          return;
        }
        track({ name: "setup_answered", question, picks: count });
        await queryClient.invalidateQueries({ queryKey: qk.doormanThread() });
        void queryClient.invalidateQueries({ queryKey: qk.rulesCurrent() });
        // "Who are you" writes the BIO, not the rules (ruling R-0908-2), and
        // the bio is read off the door settings — so that is what has to be
        // refetched for the settings panel to show what was just typed.
        if (question === "who") void queryClient.invalidateQueries({ queryKey: qk.doorSettings() });
        setLive((prev) => prev.filter((line) => line.kind === "proposal"));
      });
    },
    [queryClient],
  );

  const send = useCallback(() => {
    const message = draft.trim();
    if (!message || busyRef.current) return;
    /**
     * TALKING TO THE DOORMAN FOLDS THE BRIEFING (owner, 2026-08-27).
     *
     * The composer sits under the card, so a sentence sent with the briefing up
     * gets a reply the briefing is covering. Whoever just typed has chosen the
     * conversation; the card gets out of its way and leaves the bar as the way
     * back. After the guards above, so a stray Enter on an empty field — or one
     * during a turn already in flight — folds nothing.
     *
     * Here rather than deeper in, because every branch below this line is a
     * message going somewhere: the setup fast paths are answers, and an answer
     * is a sentence sent.
     */
    setRecapOpen(false);

    /**
     * ADJUSTING A CARD CLAIMS THE SEND, ahead of everything below (ruling
     * R-0915-1 (4)). The owner pressed Adjust on a proposal and is now saying
     * what to change about it: that is a conversation turn about a named card,
     * never an answer to a setup question and never a quotation. The card is
     * released here rather than when the reply lands — one press, one revision,
     * and a second sentence goes up as ordinary conversation.
     */
    if (adjusting !== null) {
      setDrafted([]);
      setAdjusting(null);
      sendMessage(message, adjusting);
      return;
    }

    const q = activeQuestion;
    const looksLikeChat = message.endsWith("?");

    // A quote makes it conversation, whatever the setup was expecting. The
    // fast paths below match an answer against a question exactly; a message
    // carrying a quotation is not that, and treating it as one would file
    // somebody's reply to the doorman as their answer to "what's your name".
    if (actions.quote) {
      setDrafted([]);
      sendMessage(actions.compose(message));
      return;
    }

    if (q && !looksLikeChat) {
      // Criteria chips: only an UNTOUCHED chip draft takes the fast path —
      // one edited character makes it an ordinary turn.
      const pure = drafted.join(", ");
      if (q.kind === "chips" && drafted.length > 0 && message === pure) {
        const picks =
          q.key === "gets"
            ? { gets: drafted }
            : {
                doesnt: drafted.map((label) => ({
                  text: label,
                  severity: q.chips.find((c) => c.label === label)?.severity ?? ("hard" as const),
                })),
              };
        return applySetup(message, q.key, picks, drafted.length);
      }
      // Text questions CLAIM the send while they are on the table: the
      // composer is the answer box, and a question mark is the way out.
      if (q.key === "who" && message.length <= 280) {
        return applySetup(message, q.key, { who: message }, 1);
      }
      if (q.key === "name" && message.length <= 40) {
        return applySetup(message, q.key, { name: message }, 1);
      }
      if (q.key === "tone" && message.length <= 80) {
        return applySetup(message, q.key, { tone: message }, 1);
      }
      if (q.key === "cap") {
        // Parseable answers only; anything else is conversation.
        if (/^no\s*cap[.!]?$/i.test(message)) {
          return applySetup(message, q.key, { cap: null }, 1);
        }
        const n = /^(\d{1,3})$/.exec(message);
        if (n) return applySetup(message, q.key, { cap: Number(n[1]) }, 1);
      }
    }
    setDrafted([]);
    sendMessage(message);
  }, [actions, activeQuestion, adjusting, applySetup, draft, drafted, sendMessage]);

  const answer = useCallback(
    (proposalId: string, verdict: "accepted" | "dismissed") => {
      void (async () => {
        const r =
          verdict === "accepted"
            ? await acceptSurfacedProposal(proposalId)
            : await dismissProposal(proposalId);
        if (!r.ok) {
          setError(RETRY_COPY);
          return;
        }
        setResolved((prev) => ({ ...prev, [proposalId]: verdict }));
        // A card answered outright is no longer a card to revise: the composer
        // stops pointing at it, or the next sentence would go up naming a
        // proposal the server has already settled.
        setAdjusting((current) => (current === proposalId ? null : current));
        void queryClient.invalidateQueries({ queryKey: qk.rulesCurrent() });
        void queryClient.invalidateQueries({ queryKey: qk.proposals() });
        // An acceptance ends the scripted setup server-side; the thread read
        // is what carries that fact (and the new version) back here.
        await queryClient.invalidateQueries({ queryKey: qk.doormanThread() });
        /**
         * A VERDICT ENDS IT (ruling R-0915-1 (3)). There is no next section to
         * ask for: the House Rules conversation proposes ONE batch covering
         * everything it established, and the owner's verdict on that card is
         * the end of the run. Nothing is sent from here.
         */
      })();
    },
    [queryClient],
  );

  /**
   * ADJUST — the third verb, on either kind of card, and it writes NOTHING
   * (ruling R-0915-1 (4)).
   *
   * It used to open the rules editor on the proposed document, and before that
   * it posted a dismiss. Both were doors out of the conversation, and the
   * product's whole claim is that the owner never has to leave it: the document
   * is Nob's to write and the owner's to judge. So the verb does the smallest
   * thing that can be true — it points the composer at this card and waits. No
   * request is made, nothing is settled, and the card keeps its verbs until the
   * revision comes back to supersede it or the owner answers it outright.
   *
   * The draft is CLEARED rather than seeded with the change note: the owner is
   * about to say what is wrong with that sentence, not to retype it.
   */
  const adjust = useCallback((proposalId: string) => {
    setAdjusting(proposalId);
    setDrafted([]);
    setDraft("");
    field.current?.focus();
  }, []);

  /**
   * The same verdict, on the other kind of card. Its own callback rather than a
   * branch inside `answer`: a bio accept is a different endpoint writing a
   * different thing (FR-3.8.1), and the two must not be one call that decides.
   * Neither sends the text — the proposal holds it, and the server re-screens.
   */
  const answerBio = useCallback(
    (
      proposalId: string,
      verdict: "accepted" | "dismissed",
      target: "bio" | "public_line" = "bio",
    ) => {
      void (async () => {
        const r =
          target === "public_line"
            ? verdict === "accepted"
              ? await acceptPublicLineProposal(proposalId)
              : await dismissPublicLineProposal(proposalId)
            : verdict === "accepted"
              ? await acceptBioProposal(proposalId)
              : await dismissBioProposal(proposalId);
        if (!r.ok) {
          if (r.code === "UNAUTHENTICATED") return expired.current();
          setError(RETRY_COPY);
          return;
        }
        setResolved((prev) => ({ ...prev, [proposalId]: verdict }));
        setAdjusting((current) => (current === proposalId ? null : current));
        // The door's own card and the settings form both read the bio, and the
        // public line too.
        void queryClient.invalidateQueries({ queryKey: qk.doorSettings() });
        void queryClient.invalidateQueries({ queryKey: ["door"] });
        await queryClient.invalidateQueries({ queryKey: qk.doormanThread() });
        /**
         * A BIO CARD IS A BIO CARD (ruling R-0915-1). The conversation no
         * longer opens on a fixed bio step, so a verdict here asks for nothing
         * next: the owner settles the sentence and the conversation goes on in
         * whatever direction it was already going.
         */
      })();
    },
    [queryClient],
  );

  const loadEarlier = useCallback(() => {
    const t = thread.data?.ok ? thread.data.thread : null;
    if (!t || loadingEarlier) return;
    const oldest = earlier[0]?.id ?? t.messages[0]?.id;
    if (!oldest) return;
    setLoadingEarlier(true);
    void fetchDoormanThread(oldest).then((r) => {
      setLoadingEarlier(false);
      if (!r.ok) return;
      setEarlier((prev) => [...r.thread.messages, ...prev]);
      if (r.thread.messages.length < 50) setMaybeMore(false);
    });
  }, [earlier, loadingEarlier, thread.data]);

  if (thread.isPending) {
    return <p className="nob-breathe px-4 py-10 text-sm text-faint">Waking your doorman.</p>;
  }
  if (!thread.data || !thread.data.ok) {
    return (
      <div className="px-4 py-10">
        <p className="text-sm text-faint" role="alert">
          Your doorman is not answering. Try that again.
        </p>
        <p className="mt-4">
          <LinkButton type="button" onClick={() => void thread.refetch()}>
            Try again
          </LinkButton>
        </p>
      </div>
    );
  }
  const t = thread.data.thread;
  /**
   * FACES BESIDE BUBBLES (wave 90). The owner is the reader of this thread, so
   * only Nob's runs are faced: Nob's face beside the last bubble of each run,
   * the persisted rows, the live ones, and the wait or the streaming reply all
   * counted as one sequence, so a reply that is still arriving joins the run it
   * will land in. A notice and a card belong to nobody and break a run.
   */
  const liveShown = live.filter(
    (line) =>
      (line.kind !== "proposal" && line.kind !== "bio_proposal") ||
      !t.messages.some((m) => m.proposal_id === line.proposalId),
  );
  const thinkingShown = busy && !streaming && working === null && thinkingDue;
  const persistedCount = earlier.length + t.messages.length;
  const runs = runPositions([
    ...[...earlier, ...t.messages].map(runItemOfMessage),
    ...liveShown.map(runItemOfLive),
    ...(thinkingShown || streaming ? [{ party: "nob", at: null }] : []),
  ]);
  const nobGutter = (i: number): ReactNode | null => (runs[i]?.last ? <NobBubbleFace /> : null);
  /** `metered` is the one fact that decides it — asked above, of one source. */
  const locked = metered === false;
  /** The composer's meter — none on Free, none once the period is used. */
  const meter = locked ? null : meterLine(t.plan);
  /** The server's own "used" sentence, while the period is spent (wave 61). */
  const spentPlan = plan.data?.plan;
  const spentLine =
    spentPlan?.metered && spentPlan.usage_notice === "used" ? spentPlan.usage_notice_line : null;

  /**
   * The masthead, in two parts.
   *
   * THE OPENED-DOOR SHELF SITS ABOVE EVERYTHING AND OUTSIDE THE SETUP GATE
   * (owner item 3). What is below waits for the setup wizard to finish, because
   * all of it is the OWNER's furniture and a half-configured door has nothing to
   * brief about. This is not that: it is news about somebody ELSE's door, and
   * the person most likely to be reading it is a knocker with no door of their
   * own — no wizard, no briefing, no prompts, and until now no way at all to
   * learn that the handle they queued at had opened.
   *
   * NOTHING AT ALL when there is nothing to say, and the `null` is load-bearing:
   * the pane draws the bar — padding, blur, a hairline — for any masthead it is
   * handed, so an always-present wrapper would put an empty band above every
   * thread that has no briefing and no prompts.
   */
  const shelf =
    openedDoors.length > 0 && onKnockAt ? (
      <DoorsThatOpened opened={openedDoors} onKnockAt={onKnockAt} />
    ) : null;
  const below = activeQuestion ? (
    masthead
  ) : (
    <>
      {masthead ?? <RecapBarArea open={recapOpen} onOpen={() => setRecapOpen(true)} />}
      <DoormanPrompts
        prompts={t.prompts}
        paused={doorPaused}
        onReopen={onReopenDoor}
        onShareDoor={onShareDoor}
        onEditRules={onEditRules}
      />
    </>
  );
  /**
   * THE ONE LINE A RUNNING CONVERSATION GETS (ruling R-0915-1). Under the
   * masthead, above the log, and quiet: the run has no fixed length, so there
   * is nothing to count and nothing to chart. It goes the moment the server
   * says the batch has been proposed.
   */
  const runLine = running ? (
    // The prompts' own gutter, so the line sits on the column the rest of the
    // masthead sits on rather than against the pane's edge.
    <p
      data-testid="onboarding-running"
      className="mx-auto mt-2 w-full max-w-2xl px-6 text-[13px] text-faint"
    >
      {RUNNING_COPY}
    </p>
  ) : null;
  const mastheadArea =
    shelf === null && below === null && runLine === null ? null : (
      <>
        {shelf}
        {below}
        {runLine}
      </>
    );

  return (
    <ChatShell
      fill={embedded}
      dropHandlers={drop.handlers}
      overlay={drop.dragging ? <DropOverlay /> : null}
      header={
        embedded ? null : (
          <DoorHeader name={t.persona_name} handle={t.handle} meta="your doorman" onBack={null} />
        )
      }
      /**
       * The briefing's title, hung above the scroller so it survives being read
       * past (owner, 2026-08-25) — but not while setup questions are pending,
       * for the same reason the briefing itself waits: a fresh owner should see
       * their doorman talking, not "0 knocks, none cleared the bar".
       *
       * AND THE STANDING PROMPTS UNDER IT. They were messages in the log until
       * the owner pointed out that a call to action inside a scroller is a call
       * to action you have to go and find. Up here they are always one tap away,
       * and the masthead's own minimise compacts them to a count line rather
       * than letting three CTAs hold the top of the column for ever.
       *
       * The resting-point gauntlet they used to wait behind — nothing
       * streaming, no card pending a verdict, not mid-exchange — went with the
       * move. Every one of those conditions was about not INTERRUPTING the
       * conversation, and a shelf above the conversation interrupts nothing.
       * What remains is the setup wizard: until that is done there is no
       * briefing and no shelf, only the doorman asking.
       */
      masthead={mastheadArea}
      /* The Morning Nob, laid over the conversation rather than sitting at the
         top of it (owner, 2026-08-26) — but not before there is anything to
         brief: while setup questions are pending, a fresh owner sees their
         doorman talking, not "0 knocks, none cleared the bar". */
      logOverlay={
        activeQuestion ? null : (
          <RecapArea
            open={recapOpen}
            onMinimise={() => setRecapOpen(false)}
            onOpenKnock={(knockId) => {
              setRecapOpen(false);
              onOpenKnock?.(knockId);
            }}
            onSeeAll={onSeeAll}
            onSessionExpired={onSessionExpired}
          />
        )
      }
      composer={
        locked ? (
          <LockedComposer
            onSeePlans={onSeePlans}
            trial={plan.data?.trial ?? null}
            onStartTrial={onStartTrial}
          />
        ) : (
          <Composer
            onSubmit={send}
            disabled={busy || draft.trim().length === 0}
            above={
              <>
                {/* Setup done and nothing in flight: the conversation still
                  offers a next move — standing prompts that draft into the
                  field below them, so the chat never goes dead (owner decision
                  2026-08-05). They are ABOVE THE COMPOSER and not at the foot
                  of the log, because the log is what the Morning Nob is laid
                  over: a briefing tall enough to reach its ceiling sealed all
                  four of them underneath it, and a call to action that cannot
                  be pressed is not one (F-18, 2026-09-13). Nothing here needs
                  the `!locked` guard the log copy carried — a locked composer
                  renders no `above` at all, and a chip whose whole job is to
                  fill a field that is not there would be an offer the next tap
                  cannot take up. */}
                {!activeQuestion &&
                  streaming === null &&
                  working === null &&
                  !busy &&
                  !pendingProposal &&
                  !midFlow && (
                    <DoormanSuggestions
                      disabled={busy}
                      onPick={(prompt) => {
                        setDrafted([]);
                        setDraft(prompt);
                      }}
                    />
                  )}
                {/* The clip, played back before it is sent — and, while one is
                  being made, the counter that says so. */}
                <VoicePreview voice={voice} onSend={voiceNote} />
                {pendingFile ? (
                  <PendingChip
                    filename={pendingFile.filename}
                    size={pendingFile.size}
                    uploading
                    onDismiss={dropAttachment}
                  />
                ) : null}
                {actions.quote ? (
                  <QuoteBar quote={actions.quote} onClear={actions.clearQuote} />
                ) : null}
              </>
            }
          >
            {/* The paperclip on the near side of the field and the mic on the
              far side — the hearing's arrangement, because this is the same
              composer doing the same job in a different conversation. */}
            <AttachButton onFile={attach} disabled={busy} />
            <textarea
              ref={field}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              onPaste={(e) => {
                // A screenshot is the commonest attachment there is and the
                // commonest one to lose. Only when the paste actually carried a
                // file: a paste of text stays an ordinary paste.
                const file = filesFromClipboard(e.clipboardData)[0];
                if (!file) return;
                e.preventDefault();
                attach(file);
              }}
              rows={1}
              aria-label={METER_LABEL}
              placeholder={
                // Adjusting a card claims the field, and the field says so
                // (ruling R-0915-1 (4)) — it is the only sign on the screen
                // that the next sentence is about one particular card.
                adjusting !== null
                  ? ADJUST_PLACEHOLDER
                  : activeQuestion
                    ? PLACEHOLDER_COPY[activeQuestion.key]
                    : "Ask your doorman."
              }
              disabled={busy}
              className={`${CHAT_FIELD} max-h-40 resize-none`}
            />
            <VoiceButton voice={voice} disabled={busy} />
          </Composer>
        )
      }
      footer={
        // NO METER ON AN UNMETERED TIER (ruling C10). "Nearly all of this
        // month's allowance left" is a lie told to somebody with no allowance,
        // and it is the same lie the ring was telling before it learned to stay
        // away. The locked bar under this says the true thing instead. Nothing
        // at overflow either (`meterLine`): the spent row has said it.
        meter === null ? null : (
          <p className="mt-2 font-mono text-[11px] tabular-nums text-fainter">{meter}</p>
        )
      }
    >
      {/* WHAT THIS THREAD IS, above the thread (EU AI Act Art. 50; ruling
          R-0914-1 (11)). Not a message row: a row would be something the
          doorman said, and the point of the line is that it is not. It is the
          top of the log because what arrives below it is drafts and rule
          proposals, and a disclosure a reader has to scroll back for has not
          disclosed anything. */}
      {t.disclosure ? (
        <p
          data-testid="ai-disclosure"
          className="mb-3 text-center text-[11px] leading-relaxed text-fainter"
        >
          {t.disclosure}
        </p>
      ) : null}

      {/* The Morning Nob is NOT here any more: it floats over this log rather
          than being the top of it (`logOverlay` above), so reading a message no
          longer scrolls the briefing away. */}

      {/* The way back through the conversation: pages load backwards, and the
          button retires itself when a short page proves there is no more. */}
      {maybeMore && (earlier.length > 0 || t.messages.length >= 50) && (
        <p className="mb-2 text-center">
          <QuietButton type="button" onClick={loadEarlier} disabled={loadingEarlier}>
            {loadingEarlier ? "Fetching…" : "Earlier"}
          </QuietButton>
        </p>
      )}

      {earlier.map((m, i) =>
        renderPersisted(
          m,
          resolved,
          cardSections,
          answer,
          answerBio,
          actions.open,
          adjust,
          onOpenKnock,
          nobGutter(i),
        ),
      )}

      {t.messages.map((m, i) =>
        renderPersisted(
          m,
          resolved,
          cardSections,
          answer,
          answerBio,
          actions.open,
          adjust,
          onOpenKnock,
          nobGutter(earlier.length + i),
        ),
      )}

      {liveShown
        // The live card yields to its persisted twin the moment the refetch
        // lands: the persisted notice sits IN its exchange (owner line,
        // reply, card) and now carries the diff itself — a live card kept at
        // the bottom would drift away from the question it answers.
        .map((line, i) =>
          line.kind === "attachment" ? (
            <AttachmentBubble
              key={`live-${i}`}
              attachment={line.attachment}
              onMenu={actions.open}
            />
          ) : line.kind === "bio_proposal" ? (
            <BioProposalCard
              key={`live-${i}`}
              proposalId={line.proposalId}
              changeNote={line.changeNote}
              target={line.target}
              state={resolved[line.proposalId] ?? null}
              onAnswer={answerBio}
              onAdjust={adjust}
            />
          ) : line.kind === "proposal" ? (
            <ProposalCard
              key={`live-${i}`}
              proposalId={line.proposalId}
              diff={line.diff}
              changeNote={line.changeNote}
              sections={cardSections[line.proposalId] ?? []}
              state={resolved[line.proposalId] ?? null}
              onAnswer={answer}
              onAdjust={adjust}
            />
          ) : (
            <ChatLine
              key={`live-${i}`}
              line={{ role: line.kind === "owner" ? "sender" : line.kind, content: line.content }}
              onMenu={actions.open}
              {...(line.kind === "doorman" ? { gutter: nobGutter(persistedCount + i) } : {})}
            />
          ),
        )}

      {/* The wait, in the doorman's own bubble and in the doorman's own place,
          so the reply below replaces it where it stands (owner, 2026-09-18). It
          waits `THINKING_DELAY_MS` before it appears at all (R-0915-5), and an
          empty `streaming` is nothing to show — a held-back tag fragment, not a
          reply — so the bubble stands until a character actually renders. */}
      {thinkingShown && <ThinkingBubble gutter={<NobBubbleFace />}>{THINKING_COPY}</ThinkingBubble>}
      {streaming ? <DoormanBubble gutter={<NobBubbleFace />}>{streaming}</DoormanBubble> : null}
      {working !== null && <SystemRow breathe>{working}</SystemRow>}
      {/* THE 402'S ANSWER, NOT JUST ITS SENTENCE — and not its price either
          (owner, 2026-09-23; wave 57). It was the whole plan card, the rung
          above priced and "Reset for $10"; it is the sentence and one control,
          and the control opens the plan sheet at its options, where the prices
          and the consent are. `onSeePlans` is the shell's, so a bare mount —
          which is how the interaction tier renders this component — says the
          sentence and offers nothing to press. */}
      {error &&
        (exhausted ? (
          <SpentRow line={error} plan={plan.data?.plan} onAction={onSeePlans} />
        ) : (
          <SystemRow>{error}</SystemRow>
        ))}
      {/* A SPENT PERIOD IS SAID FROM THE START, ONCE (owner, 2026-09-23; wave
          61). The grey meter line under the composer used to carry it and is
          gone at overflow, so the thread says it here — the server's line and
          the one control — before anything is refused. A refused turn does not
          add a second row: the refusal's row above stands in for this one. */}
      {!(error && exhausted) && spentLine !== null && (
        <SpentRow line={spentLine} plan={plan.data?.plan} onAction={onSeePlans} />
      )}

      {/* An upload that will not land is the room's line, not a bubble:
          nothing was said, so nothing is quoted back. */}
      <div aria-live="polite" className="contents">
        {attachError && <SystemRow>{attachError}</SystemRow>}
      </div>

      {/* The jobs a new owner has not done yet (FR-11) are NOT here any more:
          they hang in the masthead, above the scroller, where the thread cannot
          carry them off. See `DoormanPrompts` and the `masthead` slot above. */}

      {/* The standing suggestions are NOT here any more either, and for the
          third time in this file's history the reason is a layer over the log:
          they sat at the foot of the scroller, which is under the Morning Nob
          (`logOverlay`), and a briefing with two items in it is its ceiling's
          height — so every one of the four chips was behind the card and no
          press could reach them (gauntlet walk 2026-09-13 round two, F-18).
          They ride above the composer now, outside the positioned box the
          briefing is laid over. See the `composer` slot's `above`. */}

      {/* The doorman's next setup question, if the rules still leave one
          worth asking. Sits last: it is the doorman speaking, now. Chips
          draft into the composer; sending is the answer. Quiet while a
          proposal awaits its verdict — one question at a time. */}
      {!locked &&
        activeQuestion &&
        streaming === null &&
        working === null &&
        !busy &&
        !pendingProposal && (
          <SetupQuestion
            question={activeQuestion}
            drafted={new Set(drafted)}
            disabled={busy}
            onSkip={() => {
              setDrafted([]);
              setDraft("");
              // Instant hide locally; the server records it durably — Skip
              // means the DEFAULT is the answer (owner decision 2026-08-05).
              setSkippedSetup((prev) => new Set(prev).add(activeQuestion.key));
              void applyDoormanSetup({ skip: activeQuestion.key }).then(() => {
                void queryClient.invalidateQueries({ queryKey: qk.doormanThread() });
              });
            }}
            onPick={(label, mode) => {
              if (mode === "replace") {
                setDrafted([label]);
                setDraft(label);
                return;
              }
              setDrafted((prev) => [...prev, label]);
              setDraft((prev) =>
                prev.trim().length > 0 ? `${prev.replace(/,?\s*$/, "")}, ${label}` : label,
              );
            }}
          />
        )}

      {/* Announced rather than toasted — the same as the hearing's. */}
      <span className="sr-only" role="status" aria-live="polite">
        {actions.copied ? COPIED_COPY : ""}
      </span>

      {actions.menu && (
        <BubbleMenu
          target={actions.menu}
          onCopy={actions.copy}
          onQuote={actions.startQuote}
          onClose={actions.close}
        />
      )}
    </ChatShell>
  );
}

/**
 * The locked bar that stands in for the composer on Free. See `LOCKED_TITLE`.
 *
 * It carries `data-composer` so the skin's own composer rules — the ink
 * outline, the offset shadow — land on it exactly as they land on the field it
 * replaces. Without the way to the plans it is still drawn, and still says why:
 * the sentence is the point, and a surface with nowhere to send somebody is
 * better mute than absent.
 *
 * EXPORTED (R-0905-5 a): the knock chat's composer is the owner's AI work on
 * the owner's plan too, and on Free it wears this same bar rather than a copy.
 */
export function LockedComposer({
  onSeePlans,
  trial = null,
  onStartTrial,
}: {
  onSeePlans?: (() => void) | undefined;
  /**
   * THE TRIAL OFFER, WHEN `GET /plan` REPORTS ONE (ruling R-1006-2). The bar
   * grows into the gate's one centred column — the reassurance that Free still
   * hears knockers, then "Start your free trial", the offer on one line, and the
   * quiet "See the plans". The trial opens the plan room at its consent. With no
   * offer it is exactly what it was.
   */
  trial?: TrialOffer | null | undefined;
  onStartTrial?: (() => void) | undefined;
}) {
  const offer = trial && onStartTrial ? trial : null;
  const body = (
    <>
      <LockIcon className="h-4 w-4 shrink-0 text-muted" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{LOCKED_TITLE}</span>
        <span className="block truncate text-xs text-muted">{LOCKED_ACTION}</span>
      </span>
      <ForwardIcon className="h-4 w-4 shrink-0 text-muted" />
    </>
  );
  if (offer && onStartTrial) {
    return (
      <div
        data-composer
        className="nob-composer flex flex-col items-center gap-2 rounded-3xl px-4 py-4 text-center"
      >
        <span className="flex items-center gap-2 text-sm font-medium">
          <LockIcon className="h-4 w-4 shrink-0 text-muted" />
          {LOCKED_TITLE}
        </span>
        <span className="text-xs text-muted">{LOCKED_REASSURE}</span>
        <Button
          type="button"
          className="h-10 w-full max-w-xs"
          onClick={onStartTrial}
          data-trial-action
        >
          {START_TRIAL}
        </Button>
        <span className="text-xs text-muted" data-trial-line>
          {trialOfferLine(offer)}
        </span>
        {onSeePlans ? (
          <QuietButton type="button" className="h-9 w-full max-w-xs" onClick={onSeePlans}>
            {SEE_THE_PLANS}
          </QuietButton>
        ) : null}
      </div>
    );
  }
  if (!onSeePlans) {
    return (
      <div data-composer className="nob-composer flex items-center gap-3 rounded-full px-4 py-3">
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      data-composer
      onClick={onSeePlans}
      className="nob-composer nob-press flex w-full items-center gap-3 rounded-full px-4 py-3
                 text-left hover:bg-hover focus-visible:outline-none focus-visible:ring-2
                 focus-visible:ring-focus/70"
    >
      {body}
    </button>
  );
}

function renderPersisted(
  m: DoormanMessage,
  resolved: Record<string, Verdict>,
  sections: CardSections,
  onAnswer: (proposalId: string, verdict: "accepted" | "dismissed") => void,
  onAnswerBio: (
    proposalId: string,
    verdict: "accepted" | "dismissed",
    target: "bio" | "public_line",
  ) => void,
  onMenu: (target: MenuTarget) => void,
  onAdjust: (proposalId: string) => void,
  onOpenKnock?: ((knockId: string) => void) | undefined,
  /** Nob's face gutter for a doorman line (wave 90): the face on a run's last bubble. */
  nobGutter: ReactNode | null = null,
) {
  // A file the owner put in, in the place the server's clock put it — the same
  // bubble the hearing draws, so a voice note plays the same way in both.
  if (m.attachment) {
    return <AttachmentBubble key={m.id} attachment={m.attachment} onMenu={onMenu} />;
  }
  if (m.role === "notice" && m.proposal_id) {
    // The server's word on the proposal outlives this sitting; the local
    // `resolved` map only carries verdicts given seconds ago, pre-refetch.
    const settled: Verdict | null =
      resolved[m.proposal_id] ??
      (m.proposal_status === "accepted" || m.proposal_status === "dismissed"
        ? m.proposal_status
        : // The owner adjusted it and the replacement is below (R-0915-1 (4)).
          // A reload has to draw the same one-line note the live thread drew,
          // and this status is the one thing that carries the fact across it.
          m.proposal_status === "superseded"
          ? "revised"
          : null);
    // WHICH card is the server's word too (`proposal_kind`): a bio and a rules
    // change wear different verbs and write different things.
    if (m.proposal_kind === "bio" || m.proposal_kind === "public_line") {
      return (
        <BioProposalCard
          key={m.id}
          proposalId={m.proposal_id}
          changeNote={m.content}
          target={m.proposal_kind}
          state={settled}
          onAnswer={onAnswerBio}
          onAdjust={onAdjust}
        />
      );
    }
    return (
      <ProposalCard
        key={m.id}
        proposalId={m.proposal_id}
        diff={m.proposal_diff}
        changeNote={m.content}
        sections={sections[m.proposal_id] ?? []}
        state={settled}
        onAnswer={onAnswer}
        onAdjust={onAdjust}
      />
    );
  }
  /**
   * A NOTICE ABOUT A ROOM IS A DOOR, NOT A SENTENCE (ruling R-0910-7 rule 1).
   *
   * The one notice that carries a knock is the one saying a door opened for
   * this person, and the whole point of telling them is that they go and read
   * it. So it draws as a row they can press, with the news as its label and the
   * chevron that says where it leads — never as a line of prose about a
   * conversation they then have to go and find in a list.
   *
   * Without a navigator it falls back to the plain line: a row that looks
   * pressable and does nothing is worse than a sentence.
   */
  if (m.role === "notice" && m.knock_id && onOpenKnock) {
    const knockId = m.knock_id;
    return (
      <button
        key={m.id}
        type="button"
        data-open-room={knockId}
        onClick={() => onOpenKnock(knockId)}
        className="nob-press flex w-full items-center gap-3 rounded-2xl border border-accent/25
                   bg-accent/[0.03] px-4 py-3 text-left transition-colors duration-75
                   hover:bg-hover focus-visible:outline-none focus-visible:ring-2
                   focus-visible:ring-focus/70"
      >
        <span className="min-w-0 flex-1 text-[15px] leading-relaxed text-text-soft">
          {m.content}
        </span>
        <ForwardIcon className="h-4 w-4 shrink-0 text-muted" aria-hidden />
      </button>
    );
  }

  return (
    <ChatLine
      key={m.id}
      line={{ role: m.role === "owner" ? "sender" : m.role, content: m.content }}
      onMenu={onMenu}
      {...(m.role === "doorman" ? { gutter: nobGutter } : {})}
    />
  );
}

/**
 * Who spoke a persisted row, for the runs (wave 90). A file is the owner's; a
 * notice — a card, a room line, a door that opened — belongs to nobody.
 */
function runItemOfMessage(m: DoormanMessage): RunItem {
  const at = runAt(m.created_at);
  if (m.attachment || m.role === "owner") return { party: "owner", at };
  if (m.role === "doorman") return { party: "nob", at };
  return { party: null, at };
}

/** The same for a line drawn live, which has no moment of its own yet. */
function runItemOfLive(line: LiveLine): RunItem {
  if (line.kind === "owner" || line.kind === "attachment") return { party: "owner", at: null };
  if (line.kind === "doorman") return { party: "nob", at: null };
  return { party: null, at: null };
}

/**
 * THE SECTIONS A BATCH REACHES, across the head of its card (R-0915-1 (3)).
 *
 * Chips rather than a sentence, because the useful reading is a COUNT taken at
 * a glance — three chips is three places in the document about to change — and
 * a comma-separated list of four headings reads as prose nobody finishes. In
 * document order, which is the server's order: a batch that runs top to bottom
 * through the rules should look like it does.
 */
function SectionChips({ sections }: { sections: string[] }) {
  return (
    <div data-testid="proposal-sections" className="flex flex-wrap items-center gap-1.5">
      {sections.map((name) => (
        <span
          key={name}
          className="inline-flex h-5 items-center rounded-full border border-accent/30 px-2
                     font-mono text-[10px] uppercase tracking-[0.12em] text-accent-ink-strong/80"
        >
          {name}
        </span>
      ))}
    </div>
  );
}

/**
 * A bio the doorman drafted, as a card in the conversation (FR-3.8.1).
 *
 * No diff, because there is nothing to diff: the whole change is the sentence,
 * and it is printed as it would appear. Accept writes it to the door; Dismiss
 * leaves the owner's own line exactly where it was — and until one of the two,
 * nothing about this person has changed.
 */
export function BioProposalCard({
  proposalId,
  changeNote,
  target = "bio",
  state,
  onAnswer,
  onAdjust,
}: {
  proposalId: string;
  changeNote: string;
  /** The bio, or the door's public line (R-0925-2 (5)): the same card, its own words. */
  target?: "bio" | "public_line";
  state: Verdict | null;
  onAnswer: (
    proposalId: string,
    verdict: "accepted" | "dismissed",
    target: "bio" | "public_line",
  ) => void;
  /**
   * The same third verb the rules card wears (ruling R-0915-1 (4)): the owner
   * says what the sentence should say instead, in the conversation, and the
   * answer is a revised card. It used to be offered only on the walkthrough's
   * "Keep" card and used to dismiss the proposal on the way past; the card is
   * gone and so is the dismiss.
   */
  onAdjust: (proposalId: string) => void;
}) {
  if (state === "revised") return <SystemRow>{REVISED_COPY}</SystemRow>;
  const title = target === "public_line" ? PUBLIC_LINE_PROPOSED_COPY : BIO_PROPOSED_COPY;
  return (
    <div
      className="rounded-2xl border border-accent/25 bg-accent/[0.03] p-4"
      role="group"
      aria-label={title}
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent-ink-strong/70">
        {title}
      </p>
      <p className="mt-2 text-sm leading-relaxed text-text-soft">{changeNote}</p>
      {/* AI Act Art. 50(2), ruling R-0914-1 (11): the sentence in this card was
          written by a model and is about to become the owner's own bio. The
          label goes with the TEXT, not with the screen. */}
      <p className="mt-2 text-[11px] text-fainter">{AI_DRAFT_LABEL}</p>
      {state === null ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button
            type="button"
            className="h-9 px-4"
            onClick={() => onAnswer(proposalId, "accepted", target)}
          >
            {ACCEPT_COPY}
          </Button>
          <SecondaryButton type="button" className="h-9 px-4" onClick={() => onAdjust(proposalId)}>
            {ADJUST_COPY}
          </SecondaryButton>
          <QuietButton type="button" onClick={() => onAnswer(proposalId, "dismissed", target)}>
            {NOT_NOW_COPY}
          </QuietButton>
        </div>
      ) : (
        <p className="mt-3 text-[13px] text-faint">
          {state === "accepted"
            ? target === "public_line"
              ? PUBLIC_LINE_ACCEPTED_COPY
              : BIO_ACCEPTED_COPY
            : NOT_NOW_SETTLED_COPY}
        </p>
      )}
    </div>
  );
}

/**
 * A rules change, as ONE card in the conversation (ruling R-0915-1 (3)).
 *
 * A conversation ends in a batch, not a queue, so this card has to be able to
 * say how far the batch reaches: the chips across its head name every section
 * the change touches, in the order the document has them, and "four changes
 * across your rules" is a different thing to press Accept on than "one line
 * under Receipts". The names are the SERVER's — read off the two documents, not
 * guessed from the diff's lines — so a heading the owner rewrote comes back in
 * their own words.
 *
 * Live cards carry the diff; a card read back from history carries its change
 * note and hands the diff review to the Rules page, where every pending
 * proposal already lives.
 */
export function ProposalCard({
  proposalId,
  diff,
  changeNote,
  sections,
  state,
  onAnswer,
  onAdjust,
}: {
  proposalId: string;
  diff: string | null;
  changeNote: string;
  /** Every section this change touches, in document order. Empty says nothing. */
  sections: string[];
  state: Verdict | null;
  onAnswer: (proposalId: string, verdict: "accepted" | "dismissed") => void;
  /**
   * Adjust takes the id and nothing else (ruling R-0915-1 (4)): it points the
   * composer at this card, and the owner's next sentence is the revision. It
   * opened the rules editor until this ruling, and posted a dismiss before
   * that; neither happens any more.
   */
  onAdjust: (proposalId: string) => void;
}) {
  if (state === "revised") return <SystemRow>{REVISED_COPY}</SystemRow>;
  return (
    <div
      className="rounded-2xl border border-accent/25 bg-accent/[0.03] p-4"
      role="group"
      aria-label={RULES_PROPOSED_COPY}
    >
      {sections.length > 0 ? (
        <SectionChips sections={sections} />
      ) : (
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent-ink-strong/70">
          {RULES_PROPOSED_COPY}
        </p>
      )}
      <p className="mt-2 text-sm leading-relaxed text-text-soft">{changeNote}</p>
      {/* Same obligation, same sentence (R-0914-1 (11)): the change note and
          the diff below it are drafted, and accepting writes them into rules
          the owner will later read as their own. */}
      <p className="mt-2 text-[11px] text-fainter">{AI_DRAFT_LABEL}</p>
      {diff && (
        // The same rendering the Rules page uses (FR-8.3): additions in the
        // door's amber, deletions struck — a change you can read, not a
        // patch you have to decode.
        <div className="mt-3 overflow-x-auto rounded-lg border border-border bg-bg/60 py-2">
          {diffLinesFromText(diff).map((line, i) => (
            <DiffRow key={i} line={line} />
          ))}
        </div>
      )}
      {state === null ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button
            type="button"
            className="h-9 px-4"
            onClick={() => onAnswer(proposalId, "accepted")}
          >
            {ACCEPT_COPY}
          </Button>
          <SecondaryButton type="button" className="h-9 px-4" onClick={() => onAdjust(proposalId)}>
            {ADJUST_COPY}
          </SecondaryButton>
          <QuietButton type="button" onClick={() => onAnswer(proposalId, "dismissed")}>
            {NOT_NOW_COPY}
          </QuietButton>
        </div>
      ) : (
        <p className="mt-3 text-[13px] text-faint">
          {state === "accepted" ? RULES_ACCEPTED_COPY : NOT_NOW_SETTLED_COPY}
        </p>
      )}
    </div>
  );
}
